/**
 * What the cards of the course settings share: the card itself, a label/value
 * row, a text field that saves on demand, and the "configured or missing"
 * checklist of an integration.
 */
import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";

export function Card({ title, icon, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">
        {icon}{title}
      </div>
      {children}
    </div>
  );
}

export function Row({ label, value, ok, mono }) {
  // A long value — a payment endpoint's URL — wraps inside the card instead of
  // running out of it: the label keeps its width, the value takes the rest.
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="shrink-0 text-[#6B7280]">{label}</span>
      <span className={`min-w-0 text-right font-semibold ${mono ? "font-mono break-all" : "break-words"} ${ok === true ? "text-[#519872]" : ok === false ? "text-[#9b1f2c]" : "text-[#373A45]"}`}>{value}</span>
    </div>
  );
}

export function TextField({ label, value, onSave, canMutate, busy }) {
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const dirty = draft !== (value || "");
  return (
    <div className="mt-3 first:mt-0">
      <label className="text-xs font-semibold uppercase tracking-widest text-[#373A45]">{label}</label>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        disabled={!canMutate || busy !== null}
        className="mt-1 w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm text-[#373A45] focus:ring-2 focus:ring-[#965EA7] outline-none disabled:opacity-60"
      />
      {dirty && (
        <button
          onClick={() => onSave(draft)}
          disabled={!canMutate || busy !== null}
          className="mt-1 text-xs rounded-full bg-[#965EA7] text-white px-3 py-1 inline-flex items-center gap-1 hover:bg-[#7A4986] disabled:opacity-60"
        >
          <Save size={12} /> Salvează
        </button>
      )}
    </div>
  );
}

/**
 * M902 — Provider health checklist.
 *
 * Renders a compact 4–5 line "✓ Configurat / ✗ Lipsă" indicator panel above
 * the detailed key/value rows. Both Netopia and EuPlătesc cards share this
 * component to keep the visual language consistent. `items` is an array of
 * `{ label, ok }` entries; `ok=true` paints green, `false` paints red.
 */
export function HealthChecklist({ items, testid }) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mb-4 p-3 rounded-xl bg-[#F5F6F6] border border-[#E5E7EB]"
      data-testid={testid}
    >
      {items.map((it) => (
        <div
          key={it.label}
          className="flex items-center gap-2 text-xs"
          data-testid={`${testid}-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          <span
            className={
              "inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold shrink-0 " +
              (it.ok
                ? "bg-[#519872]/15 text-[#1F7A3B]"
                : "bg-[#E63946]/15 text-[#9b1f2c]")
            }
            aria-hidden="true"
          >
            {it.ok ? "✓" : "✗"}
          </span>
          <span className={"font-medium " + (it.ok ? "text-[#373A45]" : "text-[#9b1f2c]")}>
            {it.label}
          </span>
        </div>
      ))}
    </div>
  );
}
